/**
 * Profilo personale (F-090) — `/profile`, raggiungibile dal proprio nome in
 * sidebar.
 *
 * Raccoglie ciò che riguarda l'account e non i dati di investimento: anagrafica
 * di base, cambio password, sessioni aperte e registro degli accessi. Le
 * notifiche vivono qui come collegamento e non più come voce di sidebar: sono
 * un'impostazione dell'account, non una sezione da tenere sempre a un clic.
 *
 * ## Tre cose che questa pagina si rifiuta di far sembrare vere
 *
 * 1. **«Ultima rotazione» non è «ultima attività».** Gli access token durano
 *    quindici minuti: una sessione in uso continuo ruota ogni quarto d'ora, una
 *    lasciata aperta in una scheda non ruota affatto. Chiamarla «ultimo
 *    accesso» farebbe leggere come inattivo un dispositivo che sta lavorando.
 * 2. **Il registro contiene i soli accessi RIUSCITI.** Un tentativo fallito non
 *    apre alcuna sessione e non lascia traccia qui. Senza dirlo, un elenco
 *    pulito farebbe concludere che nessuno ha provato a entrare.
 * 3. **La storia non è perpetua.** La ritenzione dei token limita l'orizzonte, e
 *    la pagina dichiara di quanti giorni invece di fermarsi in silenzio.
 */

import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/format';
import { Alert, Badge, Button, Card, EmptyState, Field, Spinner, TableWrap } from '@/ui';
import { useAuth } from '@/features/auth/auth-context';
import {
  useChangePassword,
  useLoginLog,
  useRevokeSession,
  useSessions,
  type LoginEvent,
  type Session,
} from './api';
import './profile.css';

/** Deve coincidere con `passwordField` di `auth.schema.ts`. Il rifiuto vero è del server. */
const MIN_PASSWORD_LENGTH = 12;

const OUTCOME_LABELS: Record<LoginEvent['outcome'], string> = {
  active: 'sessione attiva',
  ended: 'sessione terminata',
  expired: 'sessione scaduta',
};

const OUTCOME_TONES: Record<LoginEvent['outcome'], 'positive' | 'neutral'> = {
  active: 'positive',
  ended: 'neutral',
  expired: 'neutral',
};

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Errore imprevisto';
}

function AccountCard() {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <Card title="Account">
      <dl className="profile__facts">
        <div>
          <dt>Email</dt>
          <dd>{user.email}</dd>
        </div>
        <div>
          <dt>Nome</dt>
          <dd>{user.displayName ?? '—'}</dd>
        </div>
        <div>
          <dt>Valuta base</dt>
          <dd>{user.baseCurrency}</dd>
        </div>
        <div>
          <dt>Iscritto dal</dt>
          <dd>{formatDateTime(user.createdAt)}</dd>
        </div>
        <div>
          <dt>Ultimo accesso</dt>
          <dd>{user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'questo'}</dd>
        </div>
        <div>
          <dt>Piano</dt>
          <dd>
            {user.isPremium ? <Badge tone="positive">Premium</Badge> : <Badge>Base</Badge>}
            {user.isAdmin && <> <Badge tone="accent">Amministratore</Badge></>}
          </dd>
        </div>
      </dl>

      <p className="profile__note">
        Email, nome e valuta base non si modificano da qui: incidono su import, quotazioni e
        conversioni già registrate, e cambiarli è un intervento da amministratore.
      </p>
    </Card>
  );
}

function ChangePasswordCard() {
  const change = useChangePassword();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');

  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch = repeat.length > 0 && next !== repeat;
  const ready = current.length > 0 && next.length >= MIN_PASSWORD_LENGTH && next === repeat;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ready) return;
    change.mutate(
      { currentPassword: current, newPassword: next },
      {
        onSuccess: () => {
          // I campi si svuotano subito: una password resta in memoria del
          // browser solo per il tempo che serve a inviarla.
          setCurrent('');
          setNext('');
          setRepeat('');
        },
      },
    );
  };

  return (
    <Card title="Cambia password">
      <form className="profile__form" onSubmit={handleSubmit}>
        <Field
          label="Password attuale"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          hint="Richiesta anche a sessione aperta: un accesso rubato non deve poter cambiare la password"
        />
        <Field
          label="Nuova password"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
          {...(tooShort ? { error: `Almeno ${MIN_PASSWORD_LENGTH} caratteri` } : {})}
          hint="La lunghezza conta più della complessità: nessun requisito di simboli o maiuscole"
        />
        <Field
          label="Ripeti la nuova password"
          type="password"
          autoComplete="new-password"
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
          {...(mismatch ? { error: 'Le due password non coincidono' } : {})}
        />

        <Alert variant="info" title="Le altre sessioni verranno chiuse">
          Questa resta aperta; ogni altro dispositivo dovrà accedere di nuovo. È il senso del gesto:
          se qualcuno era entrato, lasciargli la sessione aperta lo renderebbe inutile.
        </Alert>

        <div className="profile__actions">
          <Button type="submit" variant="primary" loading={change.isPending} disabled={!ready}>
            Cambia password
          </Button>
        </div>
      </form>

      {change.isError && (
        <Alert variant="error" title="Password non cambiata">
          {errorMessage(change.error)}
        </Alert>
      )}
      {change.isSuccess && (
        <Alert variant="info" title="Password aggiornata">
          {change.data.closedSessions === 0
            ? 'Non c’erano altre sessioni da chiudere.'
            : `Chiuse ${change.data.closedSessions} altre sessioni.`}
        </Alert>
      )}
    </Card>
  );
}

function SessionRow({ session }: { session: Session }) {
  const revoke = useRevokeSession();

  return (
    <tr className={session.current ? 'profile__row--current' : undefined}>
      <td>
        <div className="profile__device">{session.device}</div>
        {session.current && <Badge tone="positive">questo dispositivo</Badge>}
        {session.userAgent && <div className="profile__ua">{session.userAgent}</div>}
      </td>
      <td className="col--secondary">{session.ipAddress ?? '—'}</td>
      <td>{formatDateTime(session.startedAt)}</td>
      <td className="col--secondary">{formatDateTime(session.lastRotatedAt)}</td>
      <td>
        <Button
          variant="ghost"
          loading={revoke.isPending}
          title={
            session.current
              ? 'Chiude questa sessione: verrai disconnessa da questo dispositivo'
              : `Chiude la sessione su ${session.device}`
          }
          onClick={() => revoke.mutate(session.id)}
        >
          Chiudi
        </Button>
        {revoke.isError && (
          <p className="profile__row-error" role="alert">
            {errorMessage(revoke.error)}
          </p>
        )}
      </td>
    </tr>
  );
}

function SessionsCard() {
  const sessions = useSessions();

  return (
    <Card
      title="Sessioni aperte"
      subtitle="Un dispositivo che ha effettuato l'accesso e non è ancora uscito"
    >
      {sessions.isPending && <Spinner label="Caricamento delle sessioni…" />}
      {sessions.isError && (
        <Alert variant="error" title="Sessioni non disponibili">
          {errorMessage(sessions.error)}
        </Alert>
      )}

      {sessions.data && sessions.data.items.length > 0 && (
        <TableWrap>
          <table className="table table--compact">
            <thead>
              <tr>
                <th>Dispositivo</th>
                <th className="col--secondary">Indirizzo IP</th>
                <th>Accesso</th>
                {/* NON «ultima attività»: vedi il commento in testa al file. */}
                <th className="col--secondary">Ultimo rinnovo</th>
                <th aria-label="Azioni" />
              </tr>
            </thead>
            <tbody>
              {sessions.data.items.map((session) => (
                <SessionRow key={session.id} session={session} />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      <p className="profile__note">
        «Ultimo rinnovo» non è «ultima attività»: la sessione si rinnova circa ogni quindici minuti
        mentre la si usa, e una scheda lasciata aperta senza toccare nulla non si rinnova affatto.
      </p>
    </Card>
  );
}

function LoginLogCard() {
  const log = useLoginLog();

  return (
    <Card title="Accessi recenti">
      {log.isPending && <Spinner label="Caricamento degli accessi…" />}
      {log.isError && (
        <Alert variant="error" title="Registro non disponibile">
          {errorMessage(log.error)}
        </Alert>
      )}

      {log.data && log.data.items.length === 0 && (
        <EmptyState icon="🔐" title="Nessun accesso registrato" description="Comparirà qui al prossimo ingresso." />
      )}

      {log.data && log.data.items.length > 0 && (
        <TableWrap>
          <table className="table table--compact">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Dispositivo</th>
                <th className="col--secondary">Indirizzo IP</th>
                <th>Esito</th>
              </tr>
            </thead>
            <tbody>
              {log.data.items.map((event) => (
                <tr key={`${event.at}-${event.ipAddress ?? ''}`}>
                  <td>{formatDateTime(event.at)}</td>
                  <td>{event.device}</td>
                  <td className="col--secondary">{event.ipAddress ?? '—'}</td>
                  <td>
                    <Badge tone={OUTCOME_TONES[event.outcome]}>{OUTCOME_LABELS[event.outcome]}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {log.data && (
        <p className="profile__note">
          Sono elencati i soli accessi <strong>riusciti</strong>: un tentativo con password sbagliata
          non apre una sessione e non compare qui. La storia copre gli ultimi{' '}
          {log.data.retentionDays} giorni.
        </p>
      )}
    </Card>
  );
}

export function ProfilePage() {
  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h1>Profilo</h1>
          <p className="page__subtitle">
            Il tuo account, la password e i dispositivi da cui hai effettuato l'accesso.
          </p>
        </div>
      </header>

      <AccountCard />

      <Card title="Notifiche">
        <p className="profile__note">
          Canali di recapito (Telegram, email) e quali eventi ti raggiungono.
        </p>
        <div className="profile__actions">
          <Link className="btn btn--secondary" to="/settings/notifications">
            Apri le impostazioni di notifica
          </Link>
        </div>
      </Card>

      <ChangePasswordCard />
      <SessionsCard />
      <LoginLogCard />
    </div>
  );
}
